/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ApiConsentState, type ApiConsentStateItem } from '@agunity/api-v4';

import Content from './Content';
import Confirmation from './Confirmation';

import { FALL_BACK_LOCAL } from '../Language';
import { useModal } from '../../redux/hooks/modal';
import { selectLocale } from '../../redux/slices/locales';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { 
  getConsentActiveList, 
  patchConsentItemState, 
  selectActiveConsent, 
  selectConsents, 
  setActiveConsentIndex
} from '../../redux/slices/global';

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { openModal, closeModal } = useModal();
  const messages = useIntl().formatMessage;
  
  const dispatch = useAppDispatch();
  const consents = useAppSelector(selectConsents);
  const defaultLocale = useAppSelector(selectLocale);
  const activeConsentIndex = useAppSelector(selectActiveConsent);

  const locale = useMemo(() => defaultLocale ?? FALL_BACK_LOCAL, [defaultLocale]);
  const filteredConsent = useMemo(() => {
    return (consents || []).filter(({ givenConsent, expiryAsOf }) => 
      givenConsent === ApiConsentState.Blank ||
      ([ApiConsentState.Agreed, ApiConsentState.Rejected].includes(givenConsent!) && expiryAsOf && new Date(expiryAsOf!) < new Date())
    );
  }, [consents]);

  // Update current consent and moved to next consent
  // If last consent modal, set activeConsentIndex to -1
  const updateConsentState = useCallback(async (consentId: number, state: ApiConsentState) => {
    try {
      await dispatch(patchConsentItemState(consentId, state));
      await dispatch(setActiveConsentIndex(activeConsentIndex + 1));

      if (filteredConsent.length === activeConsentIndex + 1) {
        await dispatch(getConsentActiveList(locale, true));
        await dispatch(setActiveConsentIndex(-1));
      }
    } catch (error) {
      console.error(error);
    } finally {
      closeModal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConsentIndex, dispatch, filteredConsent.length, locale]);

  const handleConfirm = useCallback((state: ApiConsentState, consent: ApiConsentStateItem) => {
    openModal({
      autoClose: false,
      title: messages({ id: 'CONSENT_TITLE' }),
      body: <Confirmation />,
      actions: [{
        label: messages({ id: 'CONSENT_ACTIONS_CONTINUE' }),
        onClick: async () => await updateConsentState(consent.id!, state)
      }]
    });
  }, [messages, openModal, updateConsentState]);
  
  // Show the consent modal until the last active consent
  // Stop showing the consent modal if activeConsentIndex is -1
  const showConsent = useCallback(() => {
    try { 
      if (activeConsentIndex < filteredConsent.length && activeConsentIndex >= 0) {
        const consent = filteredConsent[activeConsentIndex];

        openModal({
          autoClose: false,
          title: consent.name,
          maxHeight: 'calc(100vh - 100px)',
          body: <Content consent={consent} handleConfirm={(state) => handleConfirm(state, consent)} />,
        });
      } else if ((consents || []).length && filteredConsent.length === 0 && activeConsentIndex !== -1) {
        dispatch?.(setActiveConsentIndex(-1)); 
      }
    } catch (error) {
      console.error(error);
    }
  }, [activeConsentIndex, consents, dispatch, filteredConsent, handleConfirm, openModal]);

  useEffect(() => {
    const debounce = setTimeout(showConsent);
    return () => clearTimeout(debounce);
  }, [showConsent]);

  return <>{children}</>;
};

export default ConsentProvider;
